import React, { useState, useEffect } from 'react';
import { safeLocalStorage } from '@/lib/storage-utils';
import { 
  Building2, Globe, Mail, DollarSign, Languages, Pencil, Check, X, Trash2,
  Loader2, ExternalLink, RefreshCw, AlertCircle, ShieldCheck, Copy, CheckCircle2
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useWorkspace, Workspace } from '@/context/WorkspaceContext';
import { DeleteWorkspaceDialog } from '../DeleteWorkspaceDialog';
import { toast } from 'sonner';

interface GeneralTabProps {
  workspace: Workspace;
  isGlobalDarkMode: boolean;
}

const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
];

const LANGUAGES = [
  { code: 'en-US', name: 'English (United States)' },
  { code: 'en-GB', name: 'English (United Kingdom)' },
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'de-DE', name: 'German (Germany)' },
  { code: 'it-IT', name: 'Italian (Italy)' },
  { code: 'pt-BR', name: 'Portuguese (Brazil)' },
  { code: 'ja-JP', name: 'Japanese (Japan)' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'ko-KR', name: 'Korean (South Korea)' },
  { code: 'ar-SA', name: 'Arabic (Saudi Arabia)' },
  { code: 'hi-IN', name: 'Hindi (India)' },
  { code: 'ru-RU', name: 'Russian (Russia)' },
];

// Verification data returned from the check/add edge function
interface CfVerification {
  ownership_verification?: { name: string; type: string; value: string } | null;
  ownership_verification_http?: { http_url: string; http_body: string } | null;
  ssl_validation_records?: Array<{
    cname?: string;
    cname_target?: string;
    txt_name?: string;
    txt_value?: string;
    status?: string;
  }>;
  ssl_dcv_delegation_records?: Array<{
    cname?: string;
    cname_target?: string;
  }>;
}

export const GeneralTab: React.FC<GeneralTabProps> = ({ workspace, isGlobalDarkMode: _ }) => {
  const { currentWorkspace, workspaces, refreshWorkspaces, setCurrentWorkspace } = useWorkspace();
  const [workspaceName, setWorkspaceName] = useState(workspace.name || '');
  const [customDomain, setCustomDomain] = useState(workspace.custom_domain || '');
  const [customEmailDomain, setCustomEmailDomain] = useState(workspace.custom_email_domain || '');
  const [language, setLanguage] = useState(workspace.language || 'en-US');
  const [currency, setCurrency] = useState(workspace.currency || 'USD');

  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Sync state with props when workspace changes
  useEffect(() => {
    setWorkspaceName(workspace.name || '');
    setCustomDomain(workspace.custom_domain || '');
    setCustomEmailDomain(workspace.custom_email_domain || '');
    setLanguage(workspace.language || 'en-US');
    setCurrency(workspace.currency || 'USD');

    // Seed CF verification from DB if present
    if (workspace.custom_domain_verification_record) {
      setCfVerification(workspace.custom_domain_verification_record as CfVerification);
    }
  }, [workspace.id, workspace.custom_domain, workspace.name, workspace.custom_domain_verification_record]);

  // Custom Domain management states
  const [isManagingDomain, setIsManagingDomain] = useState(false);
  const [domainRefreshing, setDomainRefreshing] = useState(false);
  const [domainError, setDomainError] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [cfVerification, setCfVerification] = useState<CfVerification | null>(
    (workspace.custom_domain_verification_record as CfVerification) ?? null
  );

  // ─── Helper: normalize domain (strip protocol/path) ──────────────────────────
  const normalizeDomain = (value: string) => {
    return value.trim().replace(/^https?:\/\//i, '').replace(/\/.*$/, '').toLowerCase();
  };

  // ─── Main domain action handler ───────────────────────────────────────────────
  const handleCustomDomainAction = async (action: 'add' | 'check' | 'remove', hostname?: string) => {
    try {
      if (action === 'check') setDomainRefreshing(true);
      else setIsManagingDomain(true);

      setDomainError(null);

      const { data, error } = await supabase.functions.invoke('cloudflare-custom-domains', {
        body: {
          action,
          workspaceId: workspace.id,
          ...(hostname ? { hostname } : {}),
        },
      });

      if (error) {
        // Supabase wraps non-2xx responses as FunctionsHttpError.
        // The real error body (from our edge function) is in error.context.
        let realMessage = error.message;
        try {
          const body = await (error as any).context?.json?.();
          if (body?.error) realMessage = body.error;
          if (body?.details?.length) {
            const detail = body.details[0];
            realMessage = detail.message || realMessage;
          }
        } catch { /* ignore parse failures */ }
        throw new Error(realMessage);
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Unknown error from server');
      }

      // Update local CF verification state from fresh data
      if (data.verification) {
        setCfVerification(data.verification);
      }

      if (action === 'add') {
        setCustomDomain(hostname!);
        setDomainInput('');
        toast.success(`Domain "${hostname}" added! Follow the DNS instructions below.`);
      } else if (action === 'check') {
        const statusLabel =
          data.status === 'active'
            ? '✅ Domain is active!'
            : data.status === 'error'
            ? '❌ Verification error. Check your DNS records.'
            : '⏳ Still pending. DNS may take a few minutes to propagate.';
        toast.info(statusLabel);
      } else if (action === 'remove') {
        setCustomDomain('');
        setCfVerification(null);
        setDomainInput('');
        toast.success('Custom domain removed.');
      }

      await refreshWorkspaces();
    } catch (err: any) {
      console.error(`Error in domain ${action}:`, err);
      const msg = err.message || 'Action failed';
      setDomainError(msg);
      toast.error(`Failed to ${action} domain: ${msg}`);
    } finally {
      setIsManagingDomain(false);
      setDomainRefreshing(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const saveWorkspaceSettings = async (updates: Partial<Workspace>) => {
    try {
      const { error } = await supabase
        .from('workspaces')
        .update(updates)
        .eq('id', workspace.id);

      if (error) throw error;
    } catch (err: any) {
      console.error('Error saving workspace settings:', err);
      alert('Failed to save settings. Please try again.');
    }
  };

  const handleStartEdit = (field: string, currentValue: string) => {
    setEditingField(field);
    setTempValue(currentValue);
  };

  const handleSave = (field: string) => {
    let updates: Partial<Workspace> = {};

    switch (field) {
      case 'name':
        setWorkspaceName(tempValue);
        updates = { name: tempValue };
        break;
      case 'customEmailDomain':
        setCustomEmailDomain(tempValue);
        updates = { custom_email_domain: tempValue };
        break;
    }

    saveWorkspaceSettings(updates);
    setEditingField(null);
    setTempValue('');
  };

  const handleCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    saveWorkspaceSettings({ language: newLanguage });
  };

  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    saveWorkspaceSettings({ currency: newCurrency });
  };

  const getLanguageName = (code: string) => {
    return LANGUAGES.find(l => l.code === code)?.name || code;
  };

  const getCurrencyName = (code: string) => {
    return CURRENCIES.find(c => c.code === code)?.code || code;
  };

  const getCurrencyFullName = (code: string) => {
    const currency = CURRENCIES.find(c => c.code === code);
    return currency ? `${currency.code} - ${currency.name}` : code;
  };

  const handleDeleteWorkspace = async () => {
    try {
      setIsDeleting(true);

      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError || !currentUser) {
        console.error('[DELETE] User auth error:', userError);
        toast.error('Unable to verify your identity. Please log in again.');
        setIsDeleting(false);
        setShowDeleteDialog(false);
        return;
      }

      const { data: memberData, error: memberError } = await supabase
        .from('workspace_members')
        .select('role, status')
        .eq('workspace_id', workspace.id)
        .eq('user_id', currentUser.id)
        .limit(1)
        .maybeSingle();

      if (memberError) {
        toast.error(`Unable to verify workspace permissions: ${memberError.message}`);
        setIsDeleting(false);
        setShowDeleteDialog(false);
        return;
      }

      if (!memberData) {
        toast.error('You are not a member of this workspace.');
        setIsDeleting(false);
        setShowDeleteDialog(false);
        return;
      }

      if (memberData.role !== 'owner') {
        toast.error(`Only workspace owners can delete workspaces. Your role: ${memberData.role}`);
        setIsDeleting(false);
        setShowDeleteDialog(false);
        return;
      }

      if (memberData.status !== 'active') {
        toast.error('Your workspace membership is not active.');
        setIsDeleting(false);
        setShowDeleteDialog(false);
        return;
      }

      if (currentWorkspace?.id === workspace.id) {
        const otherWorkspace = workspaces.find(w => w.id !== workspace.id);
        if (otherWorkspace) {
          await setCurrentWorkspace(otherWorkspace);
          safeLocalStorage.setItem('currentWorkspaceId', otherWorkspace.id);
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      const { data: deleteData, error: deleteError } = await supabase
        .from('workspaces')
        .delete()
        .eq('id', workspace.id)
        .select();

      if (deleteError) {
        throw new Error(`Database error: ${deleteError.message} (Code: ${deleteError.code})`);
      }

      if (!deleteData || deleteData.length === 0) {
        throw new Error('Workspace deletion failed - no rows affected. You may not have permission to delete this workspace.');
      }

      setShowDeleteDialog(false);
      toast.success(`Workspace "${workspace.name}" has been permanently deleted.`);
      await refreshWorkspaces();
      await new Promise(resolve => setTimeout(resolve, 300));
      window.location.href = '?modal=settings&tab=workspace';
    } catch (err: any) {
      console.error('[DELETE] Error:', err);
      toast.error(`Failed to delete workspace: ${err.message || 'Unknown error'}. Check the console for details.`);
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleDeleteClick = () => {
    if (workspaces.length <= 1) {
      toast.error('Cannot delete the only workspace. You must have at least one workspace.');
      return;
    }
    setShowDeleteDialog(true);
  };

  // ─── Status helpers ────────────────────────────────────────────────────────────
  const domainStatus = workspace.custom_domain_status || 'pending';

  const statusConfig: Record<string, { label: string; className: string }> = {
    active: { label: 'Active', className: 'bg-success/10 text-success border border-success/20' },
    pending: { label: 'Pending', className: 'bg-warning/10 text-warning border border-warning/20' },
    error: { label: 'Error', className: 'bg-destructive/10 text-destructive border border-destructive/20' },
    none: { label: 'Not Set', className: 'bg-muted text-muted-foreground' },
  };

  const currentStatusConfig = statusConfig[domainStatus] ?? statusConfig['pending'];

  // ─── Sub-components ────────────────────────────────────────────────────────────
  const DnsRecord = ({
    label,
    type,
    host,
    value,
  }: {
    label: string;
    type: string;
    host: string;
    value: string;
  }) => (
    <div className="p-3 rounded-lg border bg-background border-border">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wide">{label}</span>
        <button
          onClick={() => copyToClipboard(value)}
          className="text-muted-foreground hover:text-foreground transition-colors"
          title="Copy value"
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div className="col-span-1 border-r border-border pr-2">
          <div className="text-muted-foreground mb-1">Type</div>
          <div className="font-mono font-bold">{type}</div>
        </div>
        <div className="col-span-1 border-r border-border px-2">
          <div className="text-muted-foreground mb-1">Host / Name</div>
          <div className="font-mono font-bold truncate" title={host}>{host || '@'}</div>
        </div>
        <div className="col-span-1 pl-2">
          <div className="text-muted-foreground mb-1">Value / Target</div>
          <div className="font-mono font-bold break-all">{value}</div>
        </div>
      </div>
    </div>
  );

  const SettingItem = ({
    icon: Icon,
    title,
    description,
    value,
    field,
    isEditing,
    onStartEdit
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    value: string;
    field: string;
    isEditing: boolean;
    onStartEdit: () => void;
  }) => (
    <div className="flex items-center justify-between py-4 border-b border-border">
      <div className="flex items-start gap-4 flex-1">
        <div className="p-2 rounded-lg bg-muted">
          <Icon className="w-5 h-5 text-muted-foreground" />
        </div>
        <div className="flex-1">
          <h3 className="font-medium mb-1 text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSave(field);
                } else if (e.key === 'Escape') {
                  handleCancel();
                }
              }}
              className="px-3 py-1.5 rounded border focus:outline-none focus:ring-2 focus:ring-workspace-accent bg-background border-input text-foreground"
              autoFocus
            />
            <button
              onClick={() => handleSave(field)}
              className="p-1.5 rounded text-success hover:bg-success/10"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={handleCancel}
              className="p-1.5 rounded text-destructive hover:bg-destructive/10"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <span className="text-sm text-foreground">{value || 'Not set'}</span>
            <button
              onClick={onStartEdit}
              className="p-1.5 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
            >
              <Pencil className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  const DropdownSettingItem = ({
    icon: Icon,
    title,
    description,
    value,
    options,
    getDisplayName,
    getFullDisplayName,
    onChange
  }: {
    icon: React.ElementType;
    title: string;
    description: string;
    value: string;
    options: Array<{ code: string; name?: string }>;
    getDisplayName: (code: string) => string;
    getFullDisplayName?: (code: string) => string;
    onChange: (value: string) => void;
  }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="flex items-center justify-between py-4 border-b border-border">
        <div className="flex items-start gap-4 flex-1">
          <div className="p-2 rounded-lg bg-muted">
            <Icon className="w-5 h-5 text-muted-foreground" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium mb-1 text-foreground">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 relative">
          <div className="relative">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center gap-2 px-3 py-1.5 rounded border bg-background border-input text-foreground hover:bg-accent transition-colors"
            >
              <span className="text-sm">{getDisplayName(value)}</span>
              <Pencil className="w-4 h-4" />
            </button>
            {isOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setIsOpen(false)}
                />
                <div className="absolute right-0 mt-2 w-64 rounded-lg border shadow-lg z-20 max-h-60 overflow-y-auto bg-popover border-border">
                  {options.map((option) => (
                    <button
                      key={option.code}
                      onClick={() => {
                        onChange(option.code);
                        setIsOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        option.code === value
                          ? 'bg-workspace-accent text-white'
                          : 'text-foreground hover:bg-accent'
                      }`}
                    >
                      {getFullDisplayName ? getFullDisplayName(option.code) : (option.name || getDisplayName(option.code))}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Determine which DNS records to show
  const showDnsInstructions = customDomain && domainStatus !== 'active';

  // Build CNAME host label for display
  const cnameHost = (() => {
    if (!customDomain) return '';
    const parts = customDomain.split('.');
    // If it's a subdomain (e.g. app.example.com) → show just the subdomain part
    // If it's a root domain (e.g. example.com) → show @
    return parts.length > 2 ? parts[0] : '@';
  })();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2 text-foreground">General</h2>
        <p className="text-sm text-muted-foreground">General workspace settings</p>
      </div>

      <div className="rounded-lg border bg-card border-border">
        <div className="p-6">
          <SettingItem
            icon={Building2}
            title="Workspace Name"
            description="The name of your workspace"
            value={workspaceName}
            field="name"
            isEditing={editingField === 'name'}
            onStartEdit={() => handleStartEdit('name', workspaceName)}
          />

          {/* ─── Custom Domain Section ─────────────────────────────────────────── */}
          <div className="py-6 border-b border-border">
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="p-2 rounded-lg bg-muted">
                <Globe className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h3 className="font-medium text-foreground">Custom Domain</h3>
                  {customDomain && (
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider ${currentStatusConfig.className}`}>
                        {currentStatusConfig.label}
                      </span>
                      <button
                        onClick={() => handleCustomDomainAction('check')}
                        disabled={domainRefreshing}
                        className="p-1.5 rounded hover:bg-accent text-muted-foreground transition-colors"
                        title="Refresh status from Cloudflare"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${domainRefreshing ? 'animate-spin' : ''}`} />
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect your own domain to white-label your workspace.
                </p>
              </div>
            </div>

            <div className="ml-14 space-y-4">
              {/* Input to add domain */}
              {!customDomain && (
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    placeholder="e.g. app.yourdomain.com"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && domainInput.trim()) {
                        handleCustomDomainAction('add', normalizeDomain(domainInput));
                      }
                    }}
                    className="flex-1 px-3 py-1.5 rounded border bg-background border-input text-foreground focus:ring-2 focus:ring-workspace-accent outline-none text-sm"
                  />
                  <button
                    onClick={() => handleCustomDomainAction('add', normalizeDomain(domainInput))}
                    disabled={isManagingDomain || !domainInput.trim()}
                    className="px-4 py-1.5 bg-workspace-accent text-white rounded-md text-sm font-medium hover:bg-workspace-accent-hover transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isManagingDomain ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Connect'}
                  </button>
                </div>
              )}

              {/* Connected domain row */}
              {customDomain && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border">
                  <div className="flex items-center gap-2 text-sm min-w-0">
                    {domainStatus === 'active' ? (
                      <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
                    ) : (
                      <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
                    )}
                    <span className="font-medium text-foreground truncate">{customDomain}</span>
                    <a
                      href={`https://${customDomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-foreground shrink-0"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                  <button
                    onClick={() => handleCustomDomainAction('remove')}
                    disabled={isManagingDomain}
                    className="text-xs text-destructive hover:underline font-medium ml-3 shrink-0 disabled:opacity-50"
                  >
                    {isManagingDomain ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Disconnect'}
                  </button>
                </div>
              )}

              {/* ─── DNS Instructions ─────────────────────────────────────────────── */}
              {showDnsInstructions && (
                <div className="p-4 rounded-lg bg-muted/40 border border-border space-y-4">
                  <div className="flex items-start gap-2 text-warning">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <p className="font-semibold text-warning-foreground text-sm">DNS Configuration Required</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Add the following DNS records to your domain registrar / DNS provider. Changes may take up to 24 hours to propagate.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Record 1: CNAME to route traffic to our app */}
                    <DnsRecord
                      label="Record 1 — Traffic Routing (CNAME)"
                      type="CNAME"
                      host={cnameHost}
                      value="app.agencyplus.io"
                    />

                    {/* Record 2: Ownership verification TXT from Cloudflare */}
                    {cfVerification?.ownership_verification && (
                      <DnsRecord
                        label="Record 2 — Ownership Verification (TXT)"
                        type={cfVerification.ownership_verification.type?.toUpperCase() || 'TXT'}
                        host={cfVerification.ownership_verification.name || ''}
                        value={cfVerification.ownership_verification.value || ''}
                      />
                    )}

                    {/* Record 3: SSL / DCV CNAME if present */}
                    {cfVerification?.ssl_dcv_delegation_records?.map((rec, i) =>
                      rec.cname && rec.cname_target ? (
                        <DnsRecord
                          key={`dcv-${i}`}
                          label={`Record ${cfVerification.ownership_verification ? 3 : 2} — SSL Certificate Validation (CNAME)`}
                          type="CNAME"
                          host={rec.cname}
                          value={rec.cname_target}
                        />
                      ) : null
                    )}

                    {/* Fallback: SSL validation records as TXT if no DCV delegation */}
                    {!cfVerification?.ssl_dcv_delegation_records?.length &&
                      cfVerification?.ssl_validation_records?.map((rec, i) =>
                        rec.txt_name && rec.txt_value ? (
                          <DnsRecord
                            key={`ssl-txt-${i}`}
                            label={`Record ${cfVerification.ownership_verification ? 3 : 2} — SSL Certificate Validation (TXT)`}
                            type="TXT"
                            host={rec.txt_name}
                            value={rec.txt_value}
                          />
                        ) : rec.cname && rec.cname_target ? (
                          <DnsRecord
                            key={`ssl-cname-${i}`}
                            label={`Record ${cfVerification.ownership_verification ? 3 : 2} — SSL Certificate Validation (CNAME)`}
                            type="CNAME"
                            host={rec.cname}
                            value={rec.cname_target}
                          />
                        ) : null
                      )
                    }

                    {/* Info box */}
                    <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-xs text-blue-400">
                      <div className="flex gap-2 items-start">
                        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
                        <p>
                          After adding all DNS records, click the <strong>↻ refresh</strong> button above to check verification status. SSL activation is automatic once DNS propagates — usually within 5–30 minutes.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Active domain confirmation */}
              {customDomain && domainStatus === 'active' && (
                <div className="p-3 rounded-lg bg-success/10 border border-success/20 text-xs text-success flex gap-2 items-center">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <span>
                    Your domain is <strong>active</strong> and SSL certificate is valid. Your workspace is accessible at{' '}
                    <a href={`https://${customDomain}`} target="_blank" rel="noopener noreferrer" className="underline">
                      https://{customDomain}
                    </a>
                  </span>
                </div>
              )}

              {/* Error display */}
              {domainError && (
                <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {domainError}
                </p>
              )}
            </div>
          </div>

          <SettingItem
            icon={Mail}
            title="Custom Email Domain"
            description="Set a custom email domain for your workspace"
            value={customEmailDomain}
            field="customEmailDomain"
            isEditing={editingField === 'customEmailDomain'}
            onStartEdit={() => handleStartEdit('customEmailDomain', customEmailDomain)}
          />

          <DropdownSettingItem
            icon={Languages}
            title="Language"
            description="Set the default language of your workspace"
            value={language}
            options={LANGUAGES}
            getDisplayName={getLanguageName}
            getFullDisplayName={getLanguageName}
            onChange={handleLanguageChange}
          />

          <DropdownSettingItem
            icon={DollarSign}
            title="Currency & Format"
            description="Set the default currency and format for Invoices and Quotes"
            value={currency}
            options={CURRENCIES}
            getDisplayName={getCurrencyName}
            getFullDisplayName={getCurrencyFullName}
            onChange={handleCurrencyChange}
          />
        </div>
      </div>

      {/* Delete Workspace Section */}
      <div className="rounded-lg border bg-card border-destructive/30">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-destructive/10">
              <Trash2 className="w-5 h-5 text-destructive" />
            </div>
            <div className="flex-1">
              <h3 className="font-medium mb-1 text-foreground">Delete Workspace</h3>
              <p className="text-sm mb-4 text-muted-foreground">
                Permanently delete this workspace and all of its data. This action cannot be undone.
              </p>
              <button
                onClick={handleDeleteClick}
                disabled={isDeleting || workspaces.length <= 1}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  workspaces.length <= 1
                    ? 'bg-muted text-muted-foreground cursor-not-allowed'
                    : isDeleting
                      ? 'bg-destructive/50 text-destructive-foreground cursor-wait'
                      : 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                }`}
              >
                {isDeleting ? 'Deleting...' : 'Delete Workspace'}
              </button>
              {workspaces.length <= 1 && (
                <p className="text-xs mt-2 text-muted-foreground">
                  You must have at least one workspace
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteWorkspaceDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteWorkspace}
        workspaceName={workspace.name}
        isDeleting={isDeleting}
        isGlobalDarkMode={false}
      />
    </div>
  );
};
